/**
 * 密碼生成 — 8 字元英數混雜（保證至少各一大寫 / 小寫 / 數字）。
 * 排除易混字（I/O/l/o/0/1）以降低操作員轉貼錯誤的機會。
 *
 * - 優先使用 `crypto.getRandomValues` 取得密碼學等級隨機（modern browsers）
 * - 不可用時 fallback `Math.random`（極舊瀏覽器，BO admin 應極少出現）
 *
 * 對應 `UserCreateSchema.passwordRule`（min 8、需大寫小寫數字各一）。
 */

const UPPER = 'ABCDEFGHJKLMNPQRSTUVWXYZ'; // 排除 I O
const LOWER = 'abcdefghijkmnpqrstuvwxyz'; // 排除 l o
const DIGIT = '23456789'; // 排除 0 1

const ALL = UPPER + LOWER + DIGIT;
const PASSWORD_LENGTH = 8;

function getRandomInt(maxExclusive: number): number {
  // 使用 crypto.getRandomValues（modern browsers + Node ≥ 16）
  const cryptoObj = (typeof globalThis !== 'undefined'
    ? (globalThis as { crypto?: Crypto }).crypto
    : undefined);
  if (cryptoObj?.getRandomValues) {
    const buf = new Uint32Array(1);
    cryptoObj.getRandomValues(buf);
    return buf[0] % maxExclusive;
  }
  // Fallback：Math.random（極舊瀏覽器）
  return Math.floor(Math.random() * maxExclusive);
}

function pickRandomChar(charset: string): string {
  return charset[getRandomInt(charset.length)];
}

/**
 * 生成 8 字元英數混雜密碼。
 * 保證至少 1 個大寫、1 個小寫、1 個數字（避免一面倒同類），其餘 5 字元從全集合抽取。
 * 最後 Fisher-Yates shuffle 打亂順序，避免「前 3 字元固定為大/小/數字」的可預測結構。
 */
export function generatePassword(): string {
  const chars: string[] = [
    pickRandomChar(UPPER),
    pickRandomChar(LOWER),
    pickRandomChar(DIGIT),
  ];
  for (let i = chars.length; i < PASSWORD_LENGTH; i++) {
    chars.push(pickRandomChar(ALL));
  }
  // Fisher-Yates shuffle（用 crypto-grade RNG）
  for (let i = chars.length - 1; i > 0; i--) {
    const j = getRandomInt(i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join('');
}
